import { useState, useEffect, useCallback, useRef } from 'react';
import { resumeApi } from '../../lib/api/resume';
import type { Resume, ResumeText } from '../../types';

interface UseResumeStatusOptions {
  resumeId: string | null;
  pollInterval?: number;
  onParseComplete?: (text: ResumeText) => void;
}

interface ResumeStatusState {
  resume: Resume | null;
  text: ResumeText | null;
  isPolling: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export function useResumeStatus({
  resumeId,
  pollInterval = 2000, // 2 seconds
  onParseComplete
}: UseResumeStatusOptions) {
  const [state, setState] = useState<ResumeStatusState>({
    resume: null,
    text: null,
    isPolling: false,
    error: null,
    lastUpdated: null
  });

  const pollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const onParseCompleteRef = useRef(onParseComplete);
  onParseCompleteRef.current = onParseComplete;

  // Clear polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const fetchResumeStatus = useCallback(async (id: string) => {
    try {
      const resume = await resumeApi.getResume(id);
      
      setState(prev => ({
        ...prev,
        resume,
        error: null,
        lastUpdated: new Date()
      }));

      // If parse is completed and we don't have text yet, fetch it
      if (resume?.status?.parse_status === 'completed' && !state.text) {
        try {
          const text = await resumeApi.getResumeText(id);
          setState(prev => ({
            ...prev,
            text
          }));
          
          // Call the completion callback
          if (onParseCompleteRef.current) {
            onParseCompleteRef.current(text);
          }
        } catch (textError) {
          console.error('Failed to fetch resume text:', textError);
          // Don't set this as a critical error - text might not be ready yet
        }
      }

      return resume;
    } catch (error) {
      console.error('Failed to fetch resume status:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to fetch resume status',
        lastUpdated: new Date()
      }));
      throw error;
    }
  }, [state.text]);

  const startPolling = useCallback((id: string) => {
    if (!id || state.isPolling) return;

    setState(prev => ({ ...prev, isPolling: true, error: null }));

    const poll = async () => {
      try {
        const resume = await fetchResumeStatus(id);
        
        // Continue polling if parse is still in progress
        const shouldContinuePolling = 
          resume?.status?.parse_status === 'pending' || 
          resume?.status?.parse_status === 'processing';

        if (shouldContinuePolling) {
          pollTimeoutRef.current = setTimeout(poll, pollInterval);
        } else {
          setState(prev => ({ ...prev, isPolling: false }));
        }
      } catch (error) {
        setState(prev => ({ ...prev, isPolling: false }));
      }
    };

    poll();
  }, [fetchResumeStatus, pollInterval, state.isPolling]);

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = undefined;
    }
    setState(prev => ({ ...prev, isPolling: false }));
  }, []);

  const refetch = useCallback(async () => {
    if (!resumeId) return;
    
    try {
      await fetchResumeStatus(resumeId);
    } catch (error) {
      // Error is already handled in fetchResumeStatus
    }
  }, [resumeId, fetchResumeStatus]);

  // Auto-start polling when resumeId changes
  useEffect(() => {
    if (resumeId && !state.isPolling) {
      // First fetch immediately
      fetchResumeStatus(resumeId).then(() => {
        // Then start polling if needed
        startPolling(resumeId);
      });
    }

    return () => {
      stopPolling();
    };
  }, [resumeId, fetchResumeStatus, startPolling, stopPolling, state.isPolling]);

  // Helper functions for status checks
  const isParseComplete = state.resume?.status?.parse_status === 'completed';
  const isParsePending = state.resume?.status?.parse_status === 'pending';
  const isParseProcessing = state.resume?.status?.parse_status === 'processing';
  const isParseFailed = state.resume?.status?.parse_status === 'failed';

  return {
    // State
    resume: state.resume,
    text: state.text,
    isPolling: state.isPolling,
    error: state.error,
    lastUpdated: state.lastUpdated,
    
    // Status checks
    isParseComplete,
    isParsePending,
    isParseProcessing,
    isParseFailed,
    
    // Actions
    startPolling: () => resumeId && startPolling(resumeId),
    stopPolling,
    refetch
  };
}